// App Bootstrap
import { getInitialRoute } from "../utilities/AppBootstrapRouteUtils";
import { getAgentProviders, getAsyncDispatcher, getAuthServiceProvider, getContext, getPersistentAsyncStorage, getRequestPrioritizer, getServiceProviders, getTelemetryContext, initDataPreFetcher, loadAndInitializeWorkers, logPlanViewSubRouteType, setupAppContext, triggerCaeGraphProbeAsync, } from "../utilities/AppBootstrapUtils";
import { addGccPathToURL, removeGccPathFromBrowserUrl, removeGccPathFromURL } from "../utilities/WebAppBootstrapUtils";
import { initializeWebVitalsTracking } from "../utilities/initializeWebVitals";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { AppRootElementId, ClientAppId, ClientFlavor, Environment, TenantRegion } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { NewsBarNewsIds } from "@ms/uno-constants/lib/local/NewsBarConstants";
import { TelemetryPlatform } from "@ms/uno-constants/lib/local/TelemetryConstants";
import { LogoutSuccessfulQueryParamValue, WebAuthEventNames, WebQueryParams, WebUrlSegments, } from "@ms/uno-constants/lib/local/WebConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Controls
import { LazyGeneralErrorView } from "@ms/planner-presentational-controls/lib/local/generalErrorView/LazyGeneralErrorView";
import { PlannerShell } from "@ms/uno-shell/lib/local/controls/plannerShell/PlannerShell";
import { applyUnoGlobalStyleOverrides } from "@ms/uno-shell/lib/local/controls/unoShell/Global.styles";
import { LandingPage } from "./LandingPage";
import { AgentFactory } from "../base/factories/AgentFactory";
import { ProductionServiceOrchestrator } from "../base/factories/ProductionServiceOrchestrator";
import { WebServiceFactory } from "./factories/WebServiceFactory";
// Flux
import { DynamicImportFailureAction } from "@ms/planner-actions/lib/local/app/DynamicImportFailureAction";
import { FluxRegistry } from "@ms/uno-fluxcore/lib/local/registry";
import { PlannerRegistryDescriptor } from "../PlannerRegistryDescriptor";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { WebAdaptor } from "@ms/uno-hostadaptors/lib/local/web/WebAdaptor";
import { logNotificationInfo } from "@ms/uno-routing";
import { ComponentLoader } from "../utilities/loader/ComponentLoader";
import { WebActionCreatorLoader } from "./loaders/WebActionCreatorLoader";
import { WebLogicModuleLoader } from "./loaders/WebLogicModuleLoader";
import { WebStoreLoader } from "./loaders/WebStoreLoader";
import { WebWorkerLoader } from "./loaders/WebWorkerLoader";
import { User } from "@ms/planner-models/lib/local/client/user/User";
// Modules
import { LogicModule } from "@ms/planner-logicmodules/lib/local/modules/LogicModule";
import { AuthModule } from "@ms/planner-logicmodules/lib/local/modules/auth/AuthModule";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { LoggerManager } from "@ms/uno-telemetry/lib/local/LoggerManager";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { WebPlatformPerformanceMarker } from "@ms/uno-telemetry/lib/local/performance/WebPlatformPerformanceMarker";
import { initializeEngagementLogger, resetLogHandler, setCurrentRouteInfoForLogs, } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { getLocale } from "@ms/uno-hostadaptors/lib/local/utils/WebAdaptorUtils";
import { readRouteFromStorage } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { loadTranslations, setHtmlTitle } from "@ms/uno-shell/lib/local/utilities/CultureUtilities";
import { sleep } from "@ms/uno-utilities/lib/local/ControlFlowUtilities";
import { setDateUtilitiesLocale } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { generateGuid, isGuid } from "@ms/uno-utilities/lib/local/Guid";
import { PageTimingKey, setPageTiming } from "@ms/uno-utilities/lib/local/PASUtilities";
import { getUserAliasFromUpn } from "@ms/uno-utilities/lib/local/StringUtilities";
import { detectActiveWebUrlPrefix, getAllWebUrlPrefixes, hasPathPrefix, reconcileMsalRedirectPath, } from "@ms/uno-utilities/lib/local/WebUrlUtilities";
import { reloadWithNoPromptCheck } from "@ms/uno-utilities/lib/local/auth";
import { MsalCacheLocation } from "@ms/uno-utilities/lib/local/auth/msal/IMsalConfiguration";
import { MsalAuthRouter } from "@ms/uno-utilities/lib/local/auth/msal/MsalAuthRouter";
import { getBrowserSessionId, getLocalStorage } from "@ms/uno-utilities/lib/local/storage/StorageUtilities";
import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Temporal } from "temporal-polyfill-lite";
/**
 * The web bootstrap function
 */
export async function bootstrap() {
    // Set up performance Marker and mark bootstrap
    const performanceMarker = new WebPlatformPerformanceMarker();
    performanceMarker.mark(MarkerTag.AppBootstrap);
    // Set up AppContext and make UNO Web PPE adjustment below
    try {
        setupAppContext();
    }
    catch (error) {
        handleInfraSetupError(error);
        throw error;
    }
    // here because AppContext lives in Constants and detectActiveWebUrlPrefix is in utilities which requires consants -> circular dependency if we try to set this within AppContext
    AppContext.setRuntimeConfig({ activeWebUrlPrefix: detectActiveWebUrlPrefix(window.location.pathname) });
    const configProvider = () => AppContext.appConfiguration;
    setPageTiming(PageTimingKey.buildId, configProvider().sessionMetaData?.clientBuild);
    initializeWebVitalsTracking(performanceMarker, configProvider);
    // Set date utilities locale
    const locale = getLocale();
    setDateUtilitiesLocale(locale);
    if (configProvider().changeGates.DisableInitializeLandingPageCultureDataOutsideBootPath) {
        // Localize translations
        await loadTranslations(locale);
        performanceMarker.mark(MarkerTag.InitializeCultureDataComplete);
    }
    setHtmlTitle();
    const queryParams = new URLSearchParams(window.location.search);
    // Grab userId/tenantId from query params
    const userId = queryParams.get(WebQueryParams.UserId) ?? undefined;
    const tenantId = queryParams.get(WebQueryParams.TenantId) ?? undefined;
    const isUnauthenticatedFlow = !userId || !tenantId;
    const plannerRouteHint = queryParams.get(WebQueryParams.PlannerRouteHint) ?? undefined;
    const logoutValue = queryParams.get(WebQueryParams.Logout) ?? "0";
    const loginHint = configProvider().flights.EnableSkipAuthUpn ? undefined : queryParams.get(WebQueryParams.Upn) ?? undefined;
    const isNoPromptCheck = !!queryParams.get(WebQueryParams.NoPrompt);
    const isOcdiRedirectSession = !!queryParams.get(WebQueryParams.OcdiRedirect);
    const isUnowebRedirectSession = !!queryParams.get(WebQueryParams.UnowebRedirect);
    const hasPlannerUIHostEntryCheck = !!queryParams.get(WebQueryParams.EnableRedirectToPlannerUIHost);
    if (hasPlannerUIHostEntryCheck) {
        removeQueryParamsFromUrl(WebQueryParams.EnableRedirectToPlannerUIHost);
    }
    // Set up the logger object for pre-authed request.
    const preAuthTelemetryContext = {
        // Using the tenantRegion here in case the tenantId was specified in the url and we know the region
        locale: configProvider().sessionMetaData.tenantRegion,
        userId: null,
        tenantId: null,
        ringId: "", // Teams ring according to Teams client SDK
        isMSA: false,
        isGuestUser: false, // Setting it to false before we login.
        platform: TelemetryPlatform.Web,
    };
    let privacyGuardContext = {
        userName: undefined,
        userAlias: undefined,
    };
    // Setup loggers
    const appName = LoggingStrings.ProductNameV2;
    const loggerManager = new LoggerManager(preAuthTelemetryContext, privacyGuardContext, configProvider, appName);
    const telemetryLoggers = loggerManager.getLoggers();
    const loggers = {
        ...telemetryLoggers,
        performanceMarker,
    };
    const preAuthMsalCorrelationId = generateGuid();
    const { hostname } = location;
    const browserSessionId = getBrowserSessionId(telemetryLoggers.traceLogger);
    loggers.traceLogger.logTrace(0x1e2417cd /* tag_4jb5n */, TraceLevel.Info, `[WebAppBootstrap] Pre-Auth correlationIds`, {
        AppLoadCorrelationId: configProvider().sessionMetaData.appLoadCorrelationId,
        SessionId: configProvider().sessionMetaData.sessionId,
        MsalCorrelationId: preAuthMsalCorrelationId,
        ServiceBuildVersion: configProvider().sessionMetaData.serverBuild,
        Hostname: hostname,
        BrowserSessionId: browserSessionId,
        isNoPromptCheck: String(isNoPromptCheck),
        PlannerRouteHint: plannerRouteHint ?? "",
    });
    const clientType = configProvider().sessionMetaData.appMetadata.clientType;
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    const environment = configProvider().sessionMetaData.environment;
    const clientVersion = configProvider().sessionMetaData.clientBuild;
    const activeWebUrlPrefix = configProvider().runtimeConfig.activeWebUrlPrefix;
    // only MTRS will have GCC subroute. New planner service host will not
    const isGccSubRoute = hasPathPrefix(window.location.pathname, `${WebUrlSegments.WebUI}/${WebUrlSegments.GccPath}`);
    const pathSuffix = isGccSubRoute ? `/${WebUrlSegments.GccPath}` : "";
    const defaultRedirectUrlPath = `/${activeWebUrlPrefix}${pathSuffix}`;
    const defaultBlankRedirectUrlPath = `/${activeWebUrlPrefix}${pathSuffix}/${WebUrlSegments.BlankMsalRedirect}`;
    const defaultBridgeRedirectUrlPath = `/${activeWebUrlPrefix}${pathSuffix}/${WebUrlSegments.RedirectBridge}`;
    const defaultBridgeRedirectUrlPath2 = `/${activeWebUrlPrefix}${pathSuffix}/${WebUrlSegments.RedirectBridge2}`;
    let postLogoutRedirectUriPath = defaultRedirectUrlPath;
    let redirectUrlPath = defaultRedirectUrlPath;
    let blankRedirectUrlPath = defaultBlankRedirectUrlPath;
    const msalSettings = configProvider().settings.auth.msalSettings;
    const disableLoginRetryWithoutPrompt = configProvider().changeGates.DisableLoginRetryWithoutPrompt;
    if (msalSettings) {
        postLogoutRedirectUriPath =
            reconcileMsalRedirectPath(msalSettings.postLogoutRedirectUrlPath, activeWebUrlPrefix) ?? defaultRedirectUrlPath;
        redirectUrlPath = reconcileMsalRedirectPath(msalSettings.redirectUrlPath, activeWebUrlPrefix) ?? defaultRedirectUrlPath;
        blankRedirectUrlPath =
            reconcileMsalRedirectPath(msalSettings.blankRedirectUrlPath, activeWebUrlPrefix) ?? defaultBlankRedirectUrlPath;
    }
    const silentRedirectUrl = constructRedirectUrl(blankRedirectUrlPath);
    // If this is the blank msal redirect page, we should not render the app.
    // This is called as a part of silent auth flow in a hidden iframe.
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#block_iframe_reload
    if (window.location.pathname === silentRedirectUrl.pathname) {
        return;
    }
    const bridgeRedirectUrl = constructRedirectUrl(defaultBridgeRedirectUrlPath);
    const bridgeRedirectUrl2 = constructRedirectUrl(defaultBridgeRedirectUrlPath2);
    // [ADO:2973511] If this is the redirect bridge page, relay the auth response to the main frame via BroadcastChannel.
    // Once the changes in MTRS to load the redirectBridge2 endpoint are rolled out, we can remove this logic.
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/redirect-bridge.md
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/v4-migration.md#cross-origin-opener-policy-coop-support
    if (window.location.pathname.toLowerCase() === bridgeRedirectUrl.pathname.toLowerCase() ||
        window.location.pathname.toLowerCase() === bridgeRedirectUrl2.pathname.toLowerCase()) {
        try {
            const pathName = window.location.pathname.toLowerCase() === bridgeRedirectUrl.pathname.toLowerCase()
                ? defaultBridgeRedirectUrlPath
                : defaultBridgeRedirectUrlPath2;
            loggers.traceLogger.logTrace(0x1e0030c4 /* tag_4adde */, TraceLevel.Info, `Called from redirect bridge page. [redirectPath=${pathName}]`);
            await MsalAuthRouter.initializeRedirectBridge(loggers);
        }
        catch (error) {
            loggers.traceLogger.logTrace(0x1e0030c3 /* tag_4addd */, TraceLevel.Error, `broadcastRedirectBridgeResponse: Failed. [redirectPath=${window.location.pathname}][errorCode=${error?.errorCode ?? ""}][subError=${error?.subError ?? ""}][errorName=${error?.name ?? ""}][correlationId=${error?.correlationId ?? ""}]`);
        }
        return;
    }
    // Initialize Msal
    try {
        const msalConfigSettings = {
            clientId: ClientAppId.MicrosoftPlannerClient,
            msalCacheLocation: MsalCacheLocation.LocalStorage,
            environment,
            clientType,
            redirectUrl: constructRedirectUrl(redirectUrlPath),
            silentRedirectUrl,
            postLogoutRedirectUrl: constructPostLogoutUri(postLogoutRedirectUriPath, tenantId),
            clientName: appName,
            clientVersion,
            correlationId: preAuthMsalCorrelationId,
            redirectBridgeUri: bridgeRedirectUrl,
        };
        const msalHelperOptions = {
            useCachedPromises: true,
            handleInteractionRequiredOnSilentLogin: !configProvider().changeGates.DisableAuthInteractionRequiredUsingPopup,
            cp1ClientCapability: !configProvider().changeGates.DisableCp1ClientCapabilityWeb,
            ssoSilentRedirectFallback: !configProvider().changeGates.DisableSsoSilentRedirectFallback,
        };
        performanceMarker.mark(MarkerTag.InitializeMSALStart, { CorrelationId: msalConfigSettings.correlationId });
        await MsalAuthRouter.initializeMsalHelper(msalConfigSettings, msalHelperOptions, loggers, configProvider, isUnauthenticatedFlow);
        performanceMarker.mark(MarkerTag.InitializeMSALComplete, { CorrelationId: msalConfigSettings.correlationId });
    }
    catch (error) {
        const shouldReloadWithNoPromptCheck = !disableLoginRetryWithoutPrompt && MsalAuthRouter.isLoginRequiredAuthError(error) && !isNoPromptCheck;
        if (shouldReloadWithNoPromptCheck) {
            loggers.traceLogger.logTrace(0x1e0d688e /* tag_4dw8o */, TraceLevel.Info, `[Bootstrap] Retry with noPrompt. [TenantId=${tenantId}][HasUserId=${!!userId}][HasLoginHint=${!!loginHint}][IsNoPromptCheck=${isNoPromptCheck}]`);
            reloadWithNoPromptCheck();
            return;
        }
        handleInfraSetupError(error, configProvider().sessionMetaData.sessionId, loggers.traceLogger);
        // Unfortunately, we can't really continue without MSAL
        throw error;
    }
    removeQueryParamsFromUrl(WebQueryParams.NoPrompt); // We can remove noPrompt now
    const isCalledByLogout = logoutValue === LogoutSuccessfulQueryParamValue;
    // If logout redirected us here with no tenantId, show the landing page.
    // If tenantId is present, we should attempt to login to that tenant after logout
    // since it means that the user was logged in and then attempted to login as guest in another tenant.
    // Only one user is signed in at a time in Uno web.
    // This matches legacy Planner web behavior.
    if (isCalledByLogout) {
        removeQueryParamsFromUrl(WebQueryParams.Logout);
        if (!tenantId) {
            showLandingPage(configProvider, loggers, tenantId);
            return;
        }
    }
    try {
        loggers.traceLogger.logTrace(0x1e2417cc /* tag_4jb5m */, TraceLevel.Info, `[Bootstrap] msal ensureLoggedIn. [tenantId=${tenantId}][hasUserId=${!!userId}][hasLoginHint=${!!loginHint}]`);
        performanceMarker.mark(MarkerTag.MSALLoginStart);
        await MsalAuthRouter.msalHelperInstance.ensureLoggedIn({
            ensureActiveAccount: true,
            forceLogin: false,
            tenantId,
            userId,
            loginHint,
            isNoPromptCheck: disableLoginRetryWithoutPrompt ? undefined : isNoPromptCheck,
        });
        performanceMarker.mark(MarkerTag.MSALLoginComplete);
    }
    catch (error) {
        const showLoginPage = !!tenantId || !!loginHint || !!error?.claims;
        const errorMessage = showLoginPage
            ? `Showing login page. [TenantId=${tenantId}][hasLoginHint=${!!loginHint}][hasClaims=${!!error?.claims}]`
            : "Displaying landing page.";
        loggers.traceLogger.logTrace(0x1e2417cb /* tag_4jb5l */, TraceLevel.Warning, `[Bootstrap] Error during login. ${errorMessage}`);
        if (showLoginPage) {
            // For Guest user take them to login screen without showing landing page
            await forceLogin(loggers, tenantId, error?.claims, loginHint);
        }
        else {
            showLandingPage(configProvider, loggers, tenantId);
        }
        return;
    }
    const activeAccount = MsalAuthRouter.msalHelperInstance.getActiveAccount();
    // If there is no active account, show the landing page
    if (!activeAccount) {
        showLandingPage(configProvider, loggers, tenantId);
        return;
    }
    // Set the telemetryRegion from the token
    if (configProvider().sessionMetaData.tenantRegion === TenantRegion.Eu && activeAccount.telemetryRegion === TenantRegion.Row) {
        loggers.traceLogger.logTrace(0x1e2417ca /* tag_4jb5k */, TraceLevel.Warning, `[Bootstrap] Telemetry region mismatch. We are not updating the region. MTRS thinks region is EU. The token says the region is ROW. TenantId: ${activeAccount.tenantId}`);
    }
    else {
        configProvider().sessionMetaData.tenantRegion = activeAccount.telemetryRegion;
    }
    // Stop the pre-authorization logger
    // This is required for compliance since the user is now logged in and could be in EU.
    // We will stop the msal logging and resume when the MsalLogger is reset below.
    MsalAuthRouter.msalHelperInstance.stopLogger();
    // If the userId, tenantId, or plannerRouteHint query params are not present, reload the page with the query params.
    if (!userId ||
        !tenantId ||
        !plannerRouteHint ||
        tenantId !== plannerRouteHint ||
        !MsalAuthRouter.msalHelperInstance.validateActiveAccount(userId, tenantId)) {
        reloadWithUserAndTenantQueryParamsIfSpecified();
        return;
    }
    // Reset any subRoutePaths that are used only for authentication routing purposes
    // For instance, /gcc subpath is a url planner wepb path that is used only for routing to MTRS retail service on PROD
    removeGccPathFromBrowserUrl();
    // Remove query param userId and PlannerRouteHint from the URL
    removeQueryParamsFromUrl(WebQueryParams.UserId, WebQueryParams.PlannerRouteHint);
    // Initialize the host adaptor
    const hostAdaptor = new WebAdaptor(configProvider, () => MsalAuthRouter.msalHelperInstance);
    const context = await getContext(hostAdaptor);
    performanceMarker.mark(MarkerTag.GetContextComplete);
    const telemetryContext = getTelemetryContext(context);
    privacyGuardContext = {
        userName: context.displayName ?? undefined,
        userAlias: context.userUpn ? getUserAliasFromUpn(context.userUpn) : undefined,
    };
    resetLogHandler(telemetryContext, privacyGuardContext, loggers.logHandler, configProvider, appName);
    // Reset msal logger
    const postAuthMsalCorrelationId = generateGuid();
    MsalAuthRouter.msalHelperInstance.setLogger(loggers, appName, clientVersion, postAuthMsalCorrelationId);
    // Set logger on host adaptor
    hostAdaptor.setLoggers(loggers);
    loggers.traceLogger.logTrace(0x1e2417c9 /* tag_4jb5j */, TraceLevel.Info, `[WebAppBootstrap] Post-Auth CorrelationIds`, {
        MsalCorrelationId: postAuthMsalCorrelationId,
    });
    // Check for mismatch on GCC vs Prod environment
    if ((environment === Environment.Prod && activeAccount.accountEnvironment === Environment.Gcc) ||
        (environment === Environment.Gcc && activeAccount.accountEnvironment === Environment.Prod)) {
        // Detected that we load page/bundle from one environment but account is the other, this should not happen for GCC/Prod.
        // If happens ensure there is a proper handling of routing/redirect for Gcc+Prod users.
        loggers.traceLogger.logTrace(0x1e2417c8 /* tag_4jb5i */, TraceLevel.Error, `[Bootstrap] Gcc vs Prod mismatch for account environment. [env=${environment}][accountEnvironment=${activeAccount.accountEnvironment}]`);
        // Enforce reload on correct endpoint for current active account
        reloadWithUserAndTenantQueryParamsIfSpecified();
        return;
    }
    // Prefetch main scopes for app load and evaluate conditional access policies if required
    prefetchAndEvaluateConditionalAccessAsync(configProvider, loggers);
    // Setup the flux registry
    const registry = new FluxRegistry(PlannerRegistryDescriptor);
    let initialRoute = getInitialRoute(clientFlavor, environment);
    const initialRouteFromDeepLink = await hostAdaptor.getInitialRoute();
    performanceMarker.mark(MarkerTag.GetInitialRouteComplete);
    if (initialRouteFromDeepLink != null) {
        initialRoute = initialRouteFromDeepLink;
    }
    setCurrentRouteInfoForLogs(initialRoute.subRouteType, initialRoute.subRoute, loggers.logHandler);
    // Setup persistent storage
    const persistentStorage = getLocalStorage(context.userId, loggers.traceLogger, configProvider);
    // Check if the combination of rollout stage and environment is unexpected
    const isCombinationUnexpected = AppContext.isRolloutStageAndEnvironmentCombinationUnexpected();
    if (isCombinationUnexpected) {
        loggers.traceLogger.logTrace(0x1e2417c7 /* tag_4jb5h */, TraceLevel.Error, `[Bootstrap] Found invalid combination of rollout stage and environment: [RolloutStage=${configProvider().settings.rolloutStage}][Environment=${configProvider().sessionMetaData.environment}`);
    }
    // Initialize Stores
    const user = User.fromUserContext(context);
    const defaultQueryParameters = hostAdaptor.getDefaultQueryParameters();
    const loader = new ComponentLoader(loggers);
    // Enable hydration only for app flavor and when not initialized from a deep link. (Tabs and components are not supported for now)
    const shouldEnableRouteHydration = clientFlavor === ClientFlavor.App && initialRouteFromDeepLink == null;
    // Hydrate initialRoute from local storage if supported and enabled
    const shouldHydrateFromStorage = configProvider().settings.routePersistenceEnabled && shouldEnableRouteHydration;
    const initialHydratedRoute = shouldHydrateFromStorage
        ? readRouteFromStorage(loggers.traceLogger, persistentStorage) ?? initialRoute
        : initialRoute;
    const storeLoader = new WebStoreLoader(loader, context, initialHydratedRoute, loggers, persistentStorage, configProvider, initialRouteFromDeepLink != null, defaultQueryParameters, user);
    // Initialize agent providers
    const agentFactory = new AgentFactory(loader);
    const agentProviders = getAgentProviders(agentFactory);
    // Initialize engagement logging
    initializeEngagementLogger(storeLoader.load(Store.Route).getCurrentSubRouteType.bind(storeLoader.load(Store.Route)), loggers.engagementLogger, appName);
    // Log relevant engagement info (which can only be done AFTER we've initialized EngagementLogger)
    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.WebApp, EngagementTelemetryConstants.Bootstrap);
    logNotificationInfo(initialRoute, loggers);
    logPlanViewSubRouteType(EngagementTelemetryConstants.WebApp, storeLoader.load(Store.Route).getCurrentSubRouteType(), loggers);
    // Dispatcher for the flux system.
    const dispatcher = getAsyncDispatcher(configProvider, registry, storeLoader, loggers);
    loader.setOnFailure((name) => dispatcher.dispatchActionAsync(new DynamicImportFailureAction()));
    // Set up request prioritizer
    const requestPrioritizer = getRequestPrioritizer(storeLoader);
    // Setup Providers in correct dependency order
    const serviceFactory = new WebServiceFactory(loader, loggers);
    const authService = getAuthServiceProvider(serviceFactory, hostAdaptor, loggers, configProvider)();
    const authModule = new AuthModule(dispatcher, loggers, authService, storeLoader.load(Store.Auth), configProvider);
    // Auth module's fetchAccessTokenAsync method needed by services
    const fetchAccessToken = authModule.fetchAccessTokenAsync.bind(authModule);
    // Auth module's refreshAccessTokenAsync method needed by services
    const refreshAccessToken = authModule.refreshAccessTokenAsync.bind(authModule);
    const serviceOrchestrator = new ProductionServiceOrchestrator(serviceFactory, requestPrioritizer, configProvider, loggers, fetchAccessToken, refreshAccessToken);
    const serviceProviders = getServiceProviders(serviceOrchestrator, authService);
    // CAE probe: Use the Graph Planner service to trigger CAE enforcement.
    // Planner v4 service hasn't implemented CAE, but Graph has. The service chain handles CAE automatically.
    const caeProbePromise = triggerCaeGraphProbeAsync(configProvider, loggers, serviceProviders.plannerGraph);
    const lmLoader = new WebLogicModuleLoader(loader, dispatcher, loggers, serviceProviders, storeLoader, hostAdaptor, configProvider, authModule);
    const acLoader = new WebActionCreatorLoader(configProvider, dispatcher, loader, lmLoader, loggers, storeLoader);
    // Trigger data pre-fetcher
    if (!configProvider().settings.disablePreFetcher) {
        initDataPreFetcher(acLoader.load(ActionCreator.DataPreFetch), initialRouteFromDeepLink, context, hostAdaptor, loggers, configProvider);
    }
    const modalActionCreator = acLoader.load(ActionCreator.Modal);
    // Register msal interaction required event handler
    registerMsalErrorHandler(modalActionCreator, loggers, tenantId, storeLoader, configProvider);
    // Setup persistent async storage
    const persistentAsyncStorage = getPersistentAsyncStorage(configProvider, context.userId, loggers);
    const workerLoader = new WebWorkerLoader(acLoader, storeLoader, (store) => storeLoader.loadForPersistence(store), loader, loggers, telemetryLoggers, activeWebUrlPrefix, persistentStorage, persistentAsyncStorage, configProvider, serviceProviders, lmLoader, hostAdaptor, dispatcher, authModule);
    loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor);
    // Apply Global styles before any classes or styles are generated.
    applyUnoGlobalStyleOverrides();
    // Register theme change handler to update theme
    hostAdaptor.registerThemeChangeHandler(async (theme) => {
        const appContextAc = acLoader.load(ActionCreator.AppContext);
        appContextAc.updateAppTheme(theme);
        // Remove this once the IHostAdaptor doesn't require a promise
        return Promise.resolve();
    });
    // Register logout event handler
    window.addEventListener(WebAuthEventNames.SuiteNavLogoutEventName, async () => {
        // If the user clicked on logout, we should logout the user and redirect to the landing page.
        // The url should not have the tenantId query param.
        const postLogoutUrl = constructPostLogoutUri(postLogoutRedirectUriPath);
        await MsalAuthRouter.msalHelperInstance.logout(postLogoutUrl);
    });
    const showDevTools = configProvider().sessionMetaData.environment === Environment.Ppe;
    // If it is a unoweb redirect session, remove the query param and log
    if (isUnowebRedirectSession) {
        removeQueryParamsFromUrl(WebQueryParams.UnowebRedirect);
        loggers.traceLogger.logTrace(0x1e2417c6 /* tag_4jb5g */, TraceLevel.Verbose, `Uno web redirect session`);
    }
    // If it is a ocdi redirect session check if we should show the news bar banner
    if (isOcdiRedirectSession) {
        // Gets ocdi redirect guid and sanitize for logging
        const ocdiRedirectGuid = validateOcdiGuid(queryParams.get(WebQueryParams.OcdiGuid));
        // Gets ocdi redirect timestamp and sanitize for logging
        const ocdiTimestampInSeconds = validateOcdiTimestamp(queryParams.get(WebQueryParams.OcdiTimestamp));
        const shouldShowOcdiRedirNewsBar = !configProvider().flights.EnableNewOcdiRedirectCheck || isNewOcdiRedirectSession(ocdiTimestampInSeconds, ocdiRedirectGuid);
        if (shouldShowOcdiRedirNewsBar) {
            const newsBarAc = acLoader.load(ActionCreator.NewsBar);
            newsBarAc.showNewsBar(NewsBarNewsIds.OcdiRedirectExperience);
            performanceMarker.mark(MarkerTag.ShowOcdiRedirNewsBarComplete);
        }
        loggers.traceLogger.logTrace(0x1e2417c5 /* tag_4jb5f */, TraceLevel.Info, `Ocdi redirect session [showBar=${shouldShowOcdiRedirNewsBar}][ocdiGuid=${ocdiRedirectGuid}][ocdiRedirTimestamp=${ocdiTimestampInSeconds}]`);
    }
    // Initialize Trouter module connection in a non-blocking fashion
    if (configProvider().flights.EnableTrouterIntegration) {
        lmLoader
            .load(LogicModule.TrouterScenario)
            .then((module) => module.initializeConnectionAsync())
            .catch((error) => {
            loggers.traceLogger.logTrace(0x1e0938d0 /* tag_4ct9q */, TraceLevel.Warning, `Failed to initialize Trouter module. [Error=${ErrorUtilities.getMessage(error)}]`);
        });
    }
    // Always cleanup all ocdi redirect query params
    removeQueryParamsFromUrl(WebQueryParams.OcdiRedirect, WebQueryParams.OcdiGuid, WebQueryParams.OcdiTimestamp);
    // Clear and unregister Uno service worker if Evo SW flight is disabled
    if (!configProvider().flights.EnableServiceWorkerInEvo) {
        clearAndUnregisterServiceWorkers(loggers);
    }
    // Wait for CAE probe to complete before rendering. The Graph Planner service's
    // TokenRefreshAndRetryAjaxClient already handles CAE flows (claims challenge → token refresh → retry).
    // In the happy path, the probe resolves during parallel setup above and this await is instant.
    await caeProbePromise;
    const rootElement = document.getElementById(AppRootElementId);
    if (!rootElement) {
        loggers.traceLogger.logTrace(0x1e15b055 /* tag_4f1bv */, TraceLevel.Error, `Element with id ${AppRootElementId} not found in the DOM.`);
    }
    const root = createRoot(rootElement);
    root.render(React.createElement(PlannerShell, { authModule: authModule, dispatcher: dispatcher, serviceProviders: serviceProviders, workerLoader: workerLoader, hostAdaptor: hostAdaptor, loggers: loggers, onLoad: hostAdaptor.notifyLoaded, onSuccess: () => { }, onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider, showDevTools: showDevTools, agentProviders: agentProviders, acLoader: acLoader, storeLoader: storeLoader, isDevMode: false }));
}
/**
 * Validates and sanitizes OCDI redirect GUID
 * @param rawValue The raw GUID value from query parameters
 * @returns Sanitized GUID or empty string if invalid
 */
function validateOcdiGuid(rawValue) {
    return rawValue != null && isGuid(rawValue) ? rawValue : "";
}
/**
 * Validates and sanitizes OCDI timestamp value
 * @param rawValue The raw timestamp number from query parameters
 * @returns Sanitized ocdi timestamp unit time in seconds or -1 if invalid
 */
function validateOcdiTimestamp(rawValue) {
    const rawValueInt = parseInt(rawValue || "0", 10);
    return isNaN(rawValueInt) ? -1 : rawValueInt;
}
/**
 * Calculates time difference between OCDI timestamp and current time
 * @param ocdiTimestampInSeconds The OCDI timestamp in seconds
 * @returns Time difference in seconds or -1 if invalid
 */
function calculateOcdiTimestampDifference(ocdiTimestampInSeconds) {
    return ocdiTimestampInSeconds > 0 ? Math.floor(Date.now() / 1000) - ocdiTimestampInSeconds : -1;
}
/**
 * Determines if this is a new OCDI redirect session based on timestamp and GUID
 * @param timestampInSeconds The OCDI redirect timestamp in seconds
 * @param guid The OCDI GUID
 * @returns Whether this is a new OCDI redirect session
 */
function isNewOcdiRedirectSession(timestampInSeconds, guid) {
    if (!guid) {
        return false;
    }
    // Maximum allowed time difference in seconds
    const maxOcdiRedirectTimestampDifferenceInSeconds = 5 * 60; // 5 minutes
    const timeDiff = calculateOcdiTimestampDifference(timestampInSeconds);
    return timeDiff > 0 && timeDiff < maxOcdiRedirectTimestampDifferenceInSeconds;
}
/**
 * Register the msal error handler
 * @param modalActionCreator Modal action creator
 * @param loggers Logger context
 * @param tenantId The tenantId
 * @param storeLoader Store loader
 * @param configProvider App configuration provider
 */
function registerMsalErrorHandler(modalActionCreator, loggers, tenantId, storeLoader, configProvider) {
    // Register the interaction required error handler
    MsalAuthRouter.msalHelperInstance.registerInteractionRequiredErrorHandler(async (claims, isPopupBlockedError) => {
        if (!configProvider().changeGates.DisableForceAuthRefreshOnInactivity) {
            const activityStore = storeLoader.load(Store.Activity);
            const lastUserActivity = activityStore?.getLastUserActivityTimestamp();
            const now = Temporal.Now.instant();
            const AutoRefreshIfInactiveMinutes = 1;
            if (lastUserActivity && now.since(lastUserActivity).total({ unit: "minutes" }) > AutoRefreshIfInactiveMinutes) {
                // When user is inactive instead of displaying the auth error modal we force the sign-in auth callback to refresh auth.
                // This normally happens when user leaves browser open for a long time and token expires
                // Result here is likely either a silent sign-in SSO to just work and session gets refreshed by AAD or AAD stops login and ask for extra auth info (i.e. MFA)
                loggers.traceLogger.logTrace(0x1e2417c4 /* tag_4jb5e */, TraceLevel.Warning, `Forcing sign-in auth callback due to inactivity[hasClaims=${!!claims}]`);
                await forceLogin(loggers, tenantId, claims);
                return;
            }
        }
        // Show auth error modal and sign the user in again with the claims from claims challenge
        modalActionCreator.openModal({
            modalType: ModalType.AuthError,
            onSignInButtonClick: async () => {
                await forceLogin(loggers, tenantId, claims);
            },
            isPopupBlockedError,
            scopes: null,
        });
    });
    // Register the popup blocked error handler
    MsalAuthRouter.msalHelperInstance.registerPopupBlockedErrorHandler((scopes, onSignInButtonClick) => {
        loggers.traceLogger.logTrace(0x1e2417c3 /* tag_4jb5d */, TraceLevel.Info, "Popup window is blocked. Showing user a dialog window to disable popup or click to login.");
        // Show auth error modal and sign the user in again with the claims from claims challenge
        modalActionCreator.openModal({
            modalType: ModalType.AuthError,
            onSignInButtonClick: () => {
                // Close the modal and sign the user in again
                modalActionCreator.closeModal();
                onSignInButtonClick();
            },
            isPopupBlockedError: true,
            scopes,
        });
    });
}
/**
 * Show the login prompt and force the user to login
 * @param loggers Logger context
 * @param tenantId Tenant Id
 * @param claimsChallenge Claims Challenge
 */
async function forceLogin(loggers, tenantId, claimsChallenge, loginHint) {
    try {
        loggers.traceLogger.logTrace(0x1e2417c2 /* tag_4jb5c */, TraceLevel.Info, `[Bootstrap] MSAL forceLogin. [TenantId: ${tenantId}][HasClaimsChallenge: ${!!claimsChallenge}][hasLoginHint: ${!!loginHint}]`);
        const isNoPromptCheck = false; // The noPromptCheck is always false here since we are forcing login for resolving claims or session expiration
        await MsalAuthRouter.msalHelperInstance.ensureLoggedIn({
            forceLogin: true,
            ensureActiveAccount: false,
            tenantId: tenantId,
            claimsChallenge,
            loginHint,
            isNoPromptCheck,
        });
        loggers.performanceMarker.mark(MarkerTag.MSALForceLoginComplete);
    }
    catch (error) {
        loggers.traceLogger.logTrace(0x1e2417c1 /* tag_4jb5b */, TraceLevel.Warning, `[Bootstrap] Error attempting login in forceLogin. [TenantId: ${tenantId}][HasClaimsChallenge: ${claimsChallenge !== undefined}][error.hasClaims: ${!!error?.claims}]`);
        handleInfraSetupError(error);
    }
}
// Prefetch asynchronously main scopes for app load and evaluate conditional access policies if required
function prefetchAndEvaluateConditionalAccessAsync(configProvider, loggers) {
    if (configProvider().changeGates.DisableBootstrapAuthPrefetchAndCAEvaluation) {
        return;
    }
    // Adding graph and Planner Client app scopes as main default to prefetch and evaluate conditional access policies during bootstrap
    // FUTURE: we can potentially update this to include required scopes for current URL, for instance, premium plan -> add project scope
    const prefetchScopes = [
        `${configProvider().settings.serviceConfigurations.graph.authResourceURI}/.default`,
        `${ClientAppId.MicrosoftPlannerClient}/.default`,
    ];
    loggers.traceLogger.logTrace(0x1e2417c0 /* tag_4jb5a */, TraceLevel.Info, `[Bootstrap] prefetch & evaluate CA. [Scopes=${prefetchScopes.join(", ")}]`);
    // Intentionally fire and forget. NOTE: it might trigger a page reload during bootstrap if conditional access policy is not met
    MsalAuthRouter.msalHelperInstance.prefetchAndEvaluateConditionalAccess(prefetchScopes);
}
/**
 * General error handler for basic infra setup error
 * Note: Failing in basic infra setup means we can't really continue
 * @param error The error with basic infra setup
 * @param correlationId The correlationId (if available)
 * @param traceLogger The trace logger (if available)
 */
function handleInfraSetupError(error, correlationId, traceLogger) {
    traceLogger?.logTrace(0x1e2417a3 /* tag_4jb49 */, TraceLevel.Error, `Error in basic infra setup. [ErrorName=${error.name}][CorrelationId=${correlationId}]`);
    const rootElement = document.getElementById(AppRootElementId);
    if (!rootElement) {
        traceLogger?.logTrace(0x1e15b054 /* tag_4f1bu */, TraceLevel.Error, `Element with id ${AppRootElementId} not found in the DOM.`);
    }
    const root = createRoot(rootElement);
    root.render(React.createElement(Suspense, { fallback: renderEmptyLoadingPage() },
        React.createElement(LazyGeneralErrorView, { correlationId: correlationId })));
}
/**
 * Renders an empty page while loading the error view
 */
function renderEmptyLoadingPage() {
    return React.createElement("div", null);
}
/**
 * Remove the query Parameters from the query string
 */
function removeQueryParamsFromUrl(...queryParams) {
    const url = new URL(window.location.href);
    // Remove query param from the URL
    for (const param of queryParams) {
        url.searchParams.delete(param);
    }
    // Update the URL without reloading the page
    history.replaceState({}, "", url);
}
/**
 * The url that the user will be redirected to after logout.
 * @param postLogoutRedirectUriPath The base post logout redirect url path
 * @param tenantId The tenantId if specified
 */
function constructPostLogoutUri(postLogoutRedirectUriPath, tenantId) {
    const postLogoutUrl = constructRedirectUrl(postLogoutRedirectUriPath);
    postLogoutUrl.searchParams.set(WebQueryParams.Logout, LogoutSuccessfulQueryParamValue);
    if (tenantId) {
        postLogoutUrl.searchParams.set(WebQueryParams.TenantId, tenantId);
    }
    return postLogoutUrl;
}
/**
 * Redirect Url constructed using specified path.
 * @param urlPath Redirect Url path specified
 */
function constructRedirectUrl(urlPath) {
    const path = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
    const redirectUrl = `${window.location.origin}${path}`;
    return new URL(redirectUrl);
}
/**
 * Add the userId and tenantId query parameters which also reloads the page.
 * This is called after the user is successfully logged in.
 */
function reloadWithUserAndTenantQueryParamsIfSpecified() {
    const account = MsalAuthRouter.msalHelperInstance.getActiveAccount();
    const { userId, tenantId, accountEnvironment } = account ?? {};
    const { environment } = AppContext.appConfiguration.sessionMetaData;
    if (!account || !userId || !tenantId) {
        return;
    }
    // Create a new URLSearchParams object from the current URL's query string
    const url = new URL(window.location.href);
    // Add or update userId and tenantId in the query parameters
    url.searchParams.set(WebQueryParams.UserId, userId);
    url.searchParams.set(WebQueryParams.TenantId, tenantId);
    url.searchParams.set(WebQueryParams.PlannerRouteHint, tenantId);
    // Setup to enable only on PROD or GCC and PPE for test
    const enableGccSubRoute = environment === Environment.Ppe || environment === Environment.Gcc || environment === Environment.Prod;
    if (enableGccSubRoute) {
        // Standard path is loading /webui path, when user is in GCC we need to append /gcc to the url during bootstrap only
        // If the user is in GCC, append GCC to the url for authentication routing purposes on PROD
        if (accountEnvironment === Environment.Gcc) {
            // Append GCC to the url for authentication routing purposes on PROD if not already there
            addGccPathToURL(url);
        }
        else {
            // Any non-gcc account we expect it for PROD - Ensure we clear /gcc from the url
            removeGccPathFromURL(url);
        }
    }
    window.location.href = url.toString();
}
/**
 * Show the landing page.
 * @param configProvider App configuration provider
 * @param loggers Logger context
 * @param tenant The tenantId
 */
async function showLandingPage(configProvider, loggers, tenant) {
    if (!configProvider().changeGates.DisableInitializeLandingPageCultureDataOutsideBootPath) {
        const locale = getLocale();
        await loadTranslations(locale);
    }
    setHtmlTitle();
    const tenantId = tenant ?? "";
    // Reset any subRoutePaths that are used only for authentication routing purposes
    // For instance, /gcc subpath is a url planner wepb path that is used only for routing to MTRS retail service on PROD
    removeGccPathFromBrowserUrl();
    // Always cleanup all ocdi redirect and PlannerRouteHint query params
    removeQueryParamsFromUrl(WebQueryParams.OcdiRedirect, WebQueryParams.OcdiGuid, WebQueryParams.OcdiTimestamp, WebQueryParams.PlannerRouteHint);
    const rootElement = document.getElementById(AppRootElementId);
    if (!rootElement) {
        loggers.traceLogger.logTrace(0x1e15b053 /* tag_4f1bt */, TraceLevel.Error, `Element with id ${AppRootElementId} not found in the DOM.`);
    }
    const root = createRoot(rootElement);
    loggers.traceLogger.logTrace(0x1e054313 /* tag_4bumt */, TraceLevel.Info, `Showing LandingPage [tenantId=${tenantId}]`);
    root.render(React.createElement(LandingPage, { onClick: async () => {
            loggers.traceLogger.logTrace(0x1e054312 /* tag_4bums */, TraceLevel.Info, `LandingPage login started [tenantId=${tenantId}]`);
            await forceLogin(loggers, tenantId);
            reloadWithUserAndTenantQueryParamsIfSpecified();
        }, loggers: loggers }));
}
/**
 * Sends a message to any running service worker registrations to clear their caches and unregisters them
 * @param loggers Logger context for telemetry
 */
async function clearAndUnregisterServiceWorkers(loggers) {
    // The specific command to send to the service worker to clear its caches and unregister
    const unregisterCommand = {
        command: "clearAndUnregister",
    };
    // Check for service workers registered under both old and new prefixes
    const serviceWorkerPaths = getAllWebUrlPrefixes().map((prefix) => `/${prefix}/service-worker.js`);
    // Check that service workers are supported
    if (!("serviceWorker" in navigator) || typeof navigator.serviceWorker.getRegistrations !== "function") {
        // no-op, service workers are not supported
        return;
    }
    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        const targetRegs = regs.filter((reg) => serviceWorkerPaths.some((swPath) => reg.installing?.scriptURL?.includes(swPath) || reg.active?.scriptURL?.includes(swPath)));
        if (targetRegs.length > 0) {
            await Promise.all(targetRegs.map(async (reg) => {
                if (reg.installing) {
                    reg.installing.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e103690 /* tag_4ed0q */, TraceLevel.Info, `Tell Uno ServiceWorker to clear out [state=${reg.installing.state}]`);
                }
                if (reg.active) {
                    reg.active.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e10368f /* tag_4ed0p */, TraceLevel.Info, `Tell Uno ServiceWorker to clear out [state=${reg.active.state}]`);
                }
                // Wait for 5s to allow the worker to clear caches before unregistering
                await sleep(5000);
                const result = await reg.unregister();
                loggers.traceLogger.logTrace(0x1e10368e /* tag_4ed0o */, TraceLevel.Info, `Uno ServiceWorker unregistration done [result=${result}]`);
            }));
            loggers.traceLogger.logTrace(0x1e10368d /* tag_4ed0n */, TraceLevel.Info, `Uno ServiceWorker unregistrations done [count=${targetRegs.length}]`);
        }
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e10368c /* tag_4ed0m */, TraceLevel.Warning, `Uno ServiceWorker unregistrations failed [reason=${ErrorUtilities.getMessage(e)}]`);
    }
}
